import { FC, HTMLProps, ReactNode } from 'react';
import cn from 'classnames';
import { FormStateSpecification } from '@creditflowresearch/uilib/Form/formStateSpecification';
import { FormError } from '@creditflowresearch/uilib/Form/FormError';
import { useRadioGroupContext } from './RadioGroupContextProvider';

import styles from './radio.module.scss';

export interface RadioProps extends Omit<HTMLProps<HTMLInputElement>, 'value' | 'label'> {
  /**
   * The value of the radio button
   */
  value: string | number | boolean,
  /**
   * The main label text displayed next to the radio button
   */
  label?: ReactNode,
  /**
   * Unique identifier for testing purposes. Default: "{value}Radio"
   */
  dataTest?: string,
  /**
   * An optional sub-label text displayed below the main label
   */
  subLabel?: ReactNode,
  /**
   * Optional CSS class name for custom styling of the radio button container
   */
  className?: string,
  /**
   * Optional CSS class name for custom styling of the radio button input
   */
  radioClassName?: string,
  /**
   * Optional CSS class name for custom styling of the radio button label
   */
  radioLabelClassName?: string,
  /**
   * Optional CSS class name for custom styling when the radio button is checked
   */
  checkedClassName?: string,
  /**
   * Optional disabled state for the radio button
   */
  disabled?: boolean,
  error?: string,
}

const Radio: FC<RadioProps> = ({
  value,
  label,
  subLabel,
  className,
  radioClassName,
  radioLabelClassName,
  checkedClassName = 'checked',
  dataTest = `${ value }Radio`,
  disabled = false,
  error,
  ...inputProps
}) => {
  const { selectedValue, onChange, name, disabled: disabledRadioGroup, isErrorMessage, meta } = useRadioGroupContext();

  const isChecked = (selectedValue !== undefined && selectedValue !== null) && value === selectedValue;

  const handleChange = () => {
    onChange?.(value);
  };

  return (
    <label
      className={ cn(styles.formRadio, className, { [checkedClassName]: isChecked }) }
      data-test={ dataTest || name }
    >
      <input
        type="radio"
        className={ cn(styles.formRadioControl, radioClassName) }
        { ...inputProps }
        value={ value?.toString() }
        checked={ isChecked }
        onChange={ handleChange }
        disabled={ disabled || disabledRadioGroup }
        data-error={ String(!!isErrorMessage || FormStateSpecification.error(meta)) }
      />
      <div className={ cn(styles.formRadioLabel, radioLabelClassName) }>
        { label && <span> { label } </span> }
        { subLabel && (<span className={ styles.subLabel }>{ subLabel }</span>) }
      </div>
      { <FormError { ...meta } error={ error } /> }
    </label>
  );
};

export default Radio;
